import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, ArrowRight, ChevronDown } from "lucide-react";
import { HERO_SLIDES, BRAND } from "@/lib/data";
import { MaskText } from "@/components/motion";
import { useEnquiry } from "@/components/Enquiry";

export default function Hero() {
  const [i, setI] = useState(0);
  const ref = useRef(null);
  const { open, waLink } = useEnquiry();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex items-center overflow-hidden grain" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img key={i} src={HERO_SLIDES[i]} alt="Luxury interior"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.6, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover" />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-24">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-gold uppercase tracking-[0.3em] text-xs md:text-sm mb-5 font-medium" data-testid="hero-tagline">
          Bengaluru&apos;s Premium Modular Kitchen &amp; Wardrobe Specialists
        </motion.p>
        <h1 className="font-display text-white font-bold text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
          <MaskText lines={["Transform Your Home", "Into A Luxury", "Living Experience"]} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="text-white/80 text-base md:text-lg mt-7 max-w-xl leading-relaxed">
          Premium Modular Kitchens, Wardrobes & Complete Interior Solutions Crafted For Modern Living.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="flex flex-wrap gap-4 mt-9">
          <Button onClick={() => open({ source: "hero" })} data-testid="hero-consult"
            className="rounded-full bg-primary hover:bg-primary/90 text-white px-7 h-12 text-base group">
            Get Free Consultation <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button asChild variant="outline" data-testid="hero-whatsapp"
            className="rounded-full h-12 px-7 text-base bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-md">
            <a href={waLink("Hi Smart Living Interiors, I'd like to know more.")} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Now</a>
          </Button>
          <Button asChild variant="outline" data-testid="hero-call"
            className="rounded-full h-12 px-7 text-base bg-transparent border-white/30 text-white hover:bg-white/10">
            <a href={`tel:${BRAND.phone}`}><Phone className="w-4 h-4 mr-2" /> Call Now</a>
          </Button>
        </motion.div>

        <div className="flex gap-2 mt-14">
          {HERO_SLIDES.map((_, idx) => (
            <button key={idx} onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`}
              className={`h-1 rounded-full transition-all ${idx === i ? "w-10 bg-primary" : "w-4 bg-white/40"}`} />
          ))}
        </div>
      </motion.div>

      <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 z-10">
        <ChevronDown className="w-6 h-6" />
      </motion.div>
    </section>
  );
}
